import React from "react";
import { DialogTrigger, Menu, MenuItem } from "react-aria-components";
import type { ExportType } from "../../../utils/chartExport";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { Popover } from "../../Popover";
import { Tray } from "../../Tray";

interface LegendItem {
  category: string;
  fill: string;
  isHidden?: boolean;
  isDisabled?: boolean;
  /**
   * Stable identity for `onToggle`, when the text shown is not one. Defaults to `category`.
   * A legend that renames its entries, or whose categories can collide, passes this so the
   * handler never parses what it displays.
   */
  id?: string;
}

export interface ChartBottomBarProps {
  items: LegendItem[];
  onExport: (type: ExportType) => void | Promise<void>;
  /**
   * Horizontal insets, in px. Both default to 0 and no engine passes them —
   * see the matching note on `ChartHeaderProps`. Passing the plot's axis margin
   * here inset Export from the card edge while the legend stayed flush left.
   */
  marginLeft?: number;
  marginRight?: number;
  isZoomed?: boolean;
  onResetZoom?: () => void;
  onLegendItemToggle?: (category: string) => void;
  /**
   * Drop the Export control, keeping the legend. Distinct from `hideBottomBar` on the engines,
   * which removes both — a chart can want the legend and not the export affordance.
   *
   * Scoped to Export alone: it shares a row with Reset Zoom, which is not an export affordance and
   * appears only while a chart IS zoomed. Hiding it alongside Export stranded the reader in a
   * zoomed view with no way back.
   */
  hideExport?: boolean;
}

const LegendItem: React.FC<{ item: LegendItem; onToggle?: (category: string) => void }> = ({ item, onToggle }) => {
  const content = (
    <>
      <div
        className="h-3 w-3 shrink-0"
        style={{ backgroundColor: item.fill, borderRadius: "1px", opacity: item.isHidden ? 0.35 : 1 }}
      />
      <span
        className={`text-sm whitespace-nowrap ${item.isHidden ? "text-text-disabled line-through" : "text-text-secondary"}`}
      >
        {item.category}
      </span>
    </>
  );

  if (!onToggle) {
    return <div className="flex items-center gap-2 shrink-0">{content}</div>;
  }

  return (
    <button
      type="button"
      className="flex items-center gap-2 shrink-0 rounded-sm focus:outline-none focus:ring-2 focus:ring-border-focus disabled:cursor-not-allowed disabled:opacity-60"
      aria-pressed={!item.isHidden}
      aria-label={`${item.isHidden ? "Show" : "Hide"} ${item.category}`}
      disabled={item.isDisabled}
      onClick={() => onToggle(item.id ?? item.category)}
    >
      {content}
    </button>
  );
};

// Two rows of `text-sm` items with the `gap-3` between them; the plot keeps the rest of the card.
const COLLAPSED_LEGEND_MAX_HEIGHT = 52;

function countClippedItems(container: HTMLElement): number {
  return Array.from(container.children).filter(
    (child) => (child as HTMLElement).offsetTop >= COLLAPSED_LEGEND_MAX_HEIGHT
  ).length;
}

const CompactLegend: React.FC<{ items: LegendItem[]; onToggle?: (category: string) => void }> = ({
  items,
  onToggle,
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [clippedCount, setClippedCount] = React.useState(0);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const count = countClippedItems(list);
      setClippedCount(count);
      if (count === 0) setIsExpanded(false);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    // A collapsed list keeps its size while its items reflow (a web font landing, a text-size change).
    for (const item of Array.from(list.children)) observer.observe(item);
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  const canCollapse = clippedCount > 0;

  return (
    <div className="flex items-start gap-3">
      <div
        ref={listRef}
        data-testid="chart-legend-items"
        // `relative` makes the list its items' offsetParent, so `offsetTop` is measured from the list's top.
        className="relative flex items-center gap-3 flex-wrap min-w-0 flex-1 overflow-hidden"
        style={isExpanded ? undefined : { maxHeight: COLLAPSED_LEGEND_MAX_HEIGHT }}
      >
        {/* Index in the key: a chart may deliberately repeat a label across series — three phases
            drawn in one colour family label only the first with the signal name — and the swatch,
            not the text, is what tells those entries apart. */}
        {items.map((item, index) => (
          <LegendItem key={`${item.category}:${index}`} item={item} onToggle={onToggle} />
        ))}
      </div>
      {canCollapse && (
        <button
          type="button"
          className="shrink-0 text-sm text-text-secondary hover:text-text-primary whitespace-nowrap underline decoration-dotted underline-offset-4"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {isExpanded ? "Show less" : `+${clippedCount} more`}
        </button>
      )}
    </div>
  );
};

/**
 * ChartBottomBar
 *
 * Chart footer component with legend and export functionality.
 * Displays color-coded legend items and provides export options for CSV, SVG, and PNG formats.
 *
 * **Smart legend display:**
 * - Desktop (≥ 640px): Shows all legend items inline with wrapping
 * - Mobile (< 640px): Shows "Legend (N)" button that opens a tray with all items
 * - Legend and action buttons always stay on a single row for clean, consistent layout
 *
 * **Responsive behavior:**
 * - Mobile: No padding, legend in tray, button text hidden (icon-only)
 * - Desktop: Chart margin padding, inline legend, full button labels
 *
 * Respects chart margins to align legend with Y-axis and export button with X-axis end.
 */
export const ChartBottomBar: React.FC<ChartBottomBarProps> = ({
  items,
  onExport,
  marginLeft: _marginLeft = 0,
  marginRight: _marginRight = 0,
  hideExport = false,
  isZoomed = false,
  onResetZoom,
  onLegendItemToggle,
}) => {
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportingType, setExportingType] = React.useState<ExportType | null>(null);
  const [isLegendTrayOpen, setIsLegendTrayOpen] = React.useState(false);
  const [isExportTrayOpen, setIsExportTrayOpen] = React.useState(false);

  const handleExport = React.useCallback(
    async (type: ExportType) => {
      setIsExporting(true);
      setExportingType(type);
      setIsExportTrayOpen(false);

      try {
        await onExport(type);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        setIsExporting(false);
        setExportingType(null);
      }
    },
    [onExport]
  );

  const handleDesktopExport = React.useCallback(
    async (key: React.Key) => {
      await handleExport(key as ExportType);
    },
    [handleExport]
  );

  return (
    <div
      /* mt-2: mt-4 left the legend floating away from the plot it labels, reading as a
         separate block rather than part of the chart. */
      className="mt-2 flex items-center justify-between gap-3 min-h-[32px]"
      style={{ marginLeft: _marginLeft, marginRight: _marginRight }}
    >
      {/* Desktop: Show inline legend */}
      <div className="hidden sm:block flex-1 min-w-0">
        <CompactLegend items={items} onToggle={onLegendItemToggle} />
      </div>

      {/* Mobile: Show separate legend and export links */}
      <div className="sm:hidden flex items-center gap-3">
        {items.length > 0 && (
          <>
            <button
              onClick={() => setIsLegendTrayOpen(true)}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors underline decoration-dotted underline-offset-4"
            >
              Legend
            </button>

            <Tray isOpen={isLegendTrayOpen} onClose={() => setIsLegendTrayOpen(false)} title="Legend">
              <div className="grid grid-cols-1 gap-3 p-4">
                {items.map((item, index) => (
                  <LegendItem key={`${item.category}:${index}`} item={item} onToggle={onLegendItemToggle} />
                ))}
              </div>
            </Tray>
          </>
        )}
      </div>

      {/* Mobile: Reset Zoom and Export actions. Both are icon-only here — the desktop row carries
          the same two with visible text — so both need a name of their own. */}
      <div className="sm:hidden flex items-center gap-2 shrink-0">
        {isZoomed && onResetZoom && (
          <Button variant="secondary" size="sm" onPress={onResetZoom} aria-label="Reset Zoom">
            <Icon name="ArrowsOut" size={16} />
          </Button>
        )}

        {!hideExport && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onPress={() => setIsExportTrayOpen(true)}
              isDisabled={isExporting}
              // "Export chart", not "Export": the desktop button carries the bare word as visible
              // text, and two controls answering to one name is worse than none.
              aria-label="Export chart"
            >
              <Icon name="Export" size={16} />
            </Button>

            <Tray
              isOpen={isExportTrayOpen}
              onClose={() => setIsExportTrayOpen(false)}
              title="Export Chart"
              contentPadding={false}
            >
              <div className="flex flex-col">
                <button
                  onClick={() => handleExport("csv")}
                  disabled={isExporting}
                  className="flex items-center gap-3 px-6 py-4 text-left hover:bg-background-hover active:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="List" size={20} className="text-text-secondary" />
                  <span className="text-sm text-text-primary">
                    {exportingType === "csv" ? "Exporting..." : "Export as CSV"}
                  </span>
                </button>
                <button
                  onClick={() => handleExport("svg")}
                  disabled={isExporting}
                  className="flex items-center gap-3 px-6 py-4 text-left hover:bg-background-hover active:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="Code" size={20} className="text-text-secondary" />
                  <span className="text-sm text-text-primary">
                    {exportingType === "svg" ? "Exporting..." : "Export as SVG"}
                  </span>
                </button>
                <button
                  onClick={() => handleExport("png")}
                  disabled={isExporting}
                  className="flex items-center gap-3 px-6 py-4 text-left hover:bg-background-hover active:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="DownloadSimple" size={20} className="text-text-secondary" />
                  <span className="text-sm text-text-primary">
                    {exportingType === "png" ? "Exporting..." : "Export as PNG"}
                  </span>
                </button>
              </div>
            </Tray>
          </>
        )}
      </div>

      {/* Desktop: Actions - always on same row */}
      <div className="hidden sm:flex items-center gap-2 shrink-0">
        {isZoomed && onResetZoom && (
          <Button variant="secondary" size="sm" onPress={onResetZoom}>
            <Icon name="ArrowsOut" size={16} />
            <span className="hidden sm:inline">Reset Zoom</span>
          </Button>
        )}
        {!hideExport && (
          <DialogTrigger>
            <Button variant="secondary" size="sm" isDisabled={isExporting}>
              <Icon name="Export" size={16} />
              {isExporting ? "Exporting..." : "Export"}
            </Button>
            <Popover
              placement="top end"
              className="min-w-48 p-1 bg-background-surface rounded-lg shadow-xl ring-1 ring-border-default"
            >
              <Menu onAction={handleDesktopExport} className="outline-none">
                <MenuItem
                  id="csv"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary rounded cursor-pointer hover:bg-background-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-1 focus-visible:ring-offset-background-surface data-[focused]:bg-background-hover data-[focused]:ring-2 data-[focused]:ring-border-focus data-[focused]:ring-offset-1 data-[focused]:ring-offset-background-surface"
                  isDisabled={isExporting}
                >
                  <span className="flex items-center gap-2">
                    <Icon name="List" size={14} />
                    {exportingType === "csv" ? "Exporting..." : "Export as CSV"}
                  </span>
                </MenuItem>
                <MenuItem
                  id="svg"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary rounded cursor-pointer hover:bg-background-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-1 focus-visible:ring-offset-background-surface data-[focused]:bg-background-hover data-[focused]:ring-2 data-[focused]:ring-border-focus data-[focused]:ring-offset-1 data-[focused]:ring-offset-background-surface"
                  isDisabled={isExporting}
                >
                  <span className="flex items-center gap-2">
                    <Icon name="Code" size={14} />
                    {exportingType === "svg" ? "Exporting..." : "Export as SVG"}
                  </span>
                </MenuItem>
                <MenuItem
                  id="png"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary rounded cursor-pointer hover:bg-background-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-1 focus-visible:ring-offset-background-surface data-[focused]:bg-background-hover data-[focused]:ring-2 data-[focused]:ring-border-focus data-[focused]:ring-offset-1 data-[focused]:ring-offset-background-surface"
                  isDisabled={isExporting}
                >
                  <span className="flex items-center gap-2">
                    <Icon name="DownloadSimple" size={14} />
                    {exportingType === "png" ? "Exporting..." : "Export as PNG"}
                  </span>
                </MenuItem>
              </Menu>
            </Popover>
          </DialogTrigger>
        )}
      </div>
    </div>
  );
};
